import {
  resolveCheckoutSessionOutcome,
  type CheckoutSessionOutcome,
} from "@baluu/ui/lib/checkout/request-to-book";

/** The fields of a createBasketCheckoutSession response the overlay routes on. */
export type BasketSessionResponse = {
  basketOrderId: string;
  amount?: number;
  isRequestToBook?: boolean;
  /** A deposit-at-request session: paid on the card form (no `isRequestToBook`). */
  requestDeposit?: boolean;
  clientSecret?: string | null;
  checkoutUrl?: string | null;
  usedStripeHostedPage?: boolean;
};

/**
 * What the basket checkout overlay does with a createBasketCheckoutSession
 * response. The shared resolver decides request / free / hosted / card from
 * the SERVER's answer alone (a request never opens a card form, a stale
 * request flag on the basket never hides one), plus this overlay's
 * long-standing fallback: a response with neither a clientSecret nor a hosted
 * URL has nothing to pay with and is confirmed like a free order.
 */
export function resolveOverlaySessionOutcome(
  result: BasketSessionResponse,
): CheckoutSessionOutcome {
  const outcome = resolveCheckoutSessionOutcome(result);
  if (outcome.kind === "embedded" && !result.clientSecret && !result.checkoutUrl) {
    return { kind: "confirm", completionVariant: "paid" };
  }
  return outcome;
}

/**
 * Confirms the order createBasketCheckoutSession just created, without Stripe
 * (free and request-to-book orders). Throws the server's error text, else
 * `failureMessage`.
 */
export async function confirmBasketOrder(
  baseUrl: string,
  basketOrderId: string,
  failureMessage: string,
): Promise<void> {
  const res = await fetch(`${baseUrl}/widget/app/confirmFreeBasketOrder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ basketOrderId }),
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.message || errData.error || failureMessage);
  }
}

/**
 * Host-page event for a request-to-book order sent from the overlay. A
 * request is not a purchase (nothing is charged until the partner confirms
 * it and the customer pays the link), so it never fires the
 * `baluu_booking_completed` conversion or the completed DOM events; this
 * distinct event lets host pages tell a request apart.
 */
export const BOOKING_REQUESTED_EVENT = "baluu_booking_requested";

export function dispatchBookingRequested(basketOrderId: string): void {
  if (typeof document === "undefined") return;
  // Dispatched on document and bubbling, so listeners on document or window
  // both receive it; composed so it crosses shadow roots like the other
  // documented events.
  document.dispatchEvent(
    new CustomEvent(BOOKING_REQUESTED_EVENT, {
      bubbles: true,
      composed: true,
      detail: { basketOrderId },
    }),
  );
}
